import { type DragEvent, useCallback, useRef, useState } from 'react';
import {
  CloudUploadOutlined as CloudUploadIcon,
  InsertDriveFileOutlined as PdfIcon,
} from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

interface ClinicDocumentDropZoneProps {
  onFilesDropped: (files: File[]) => void;
  isUploading: boolean;
}

const filterPdfs = (files: FileList | File[]): File[] =>
  Array.from(files).filter(
    f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
  );

export function ClinicDocumentDropZone({
  onFilesDropped,
  isUploading,
}: ClinicDocumentDropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      dragCounter.current = 0;

      const pdfs = filterPdfs(e.dataTransfer.files);
      if (pdfs.length > 0) {
        onFilesDropped(pdfs);
      }
    },
    [onFilesDropped],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const pdfs = filterPdfs(e.target.files ?? []);
      if (pdfs.length > 0) {
        onFilesDropped(pdfs);
      }
      // Reset so re-selecting the same file works
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [onFilesDropped],
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 3, md: 5 },
      }}
    >
      <Box
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          width: '100%',
          maxWidth: 560,
          minHeight: 340,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 4,
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : 'rgba(130, 110, 230, 0.3)',
          background: isDragOver
            ? 'linear-gradient(135deg, rgba(130,110,230,0.12) 0%, rgba(100,80,220,0.08) 100%)'
            : 'linear-gradient(135deg, rgba(130,110,230,0.06) 0%, rgba(100,80,220,0.02) 50%, rgba(200,190,255,0.06) 100%)',
          transition: 'all 0.25s ease',
          transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
          cursor: isUploading ? 'default' : 'pointer',
          pointerEvents: isUploading ? 'none' : 'auto',
          px: 4,
          py: 5,
          textAlign: 'center',
          '&:hover': {
            borderColor: 'rgba(130, 110, 230, 0.5)',
            background:
              'linear-gradient(135deg, rgba(130,110,230,0.1) 0%, rgba(100,80,220,0.05) 50%, rgba(200,190,255,0.08) 100%)',
          },
        }}
        onClick={() => !isUploading && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="Drop PDF files here or click to browse"
        onKeyDown={e => {
          if ((e.key === 'Enter' || e.key === ' ') && !isUploading) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          hidden
          multiple
          accept="application/pdf,.pdf"
          type="file"
          onChange={handleFileInput}
        />

        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: isDragOver
              ? 'linear-gradient(135deg, #836eed 0%, #6246d8 100%)'
              : 'linear-gradient(135deg, #e8e2fc 0%, #d4cbf7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            transition: 'all 0.25s ease',
          }}
        >
          {isUploading ? (
            <CircularProgress size={32} sx={{ color: '#836eed' }} />
          ) : (
            <CloudUploadIcon
              sx={{
                fontSize: 36,
                color: isDragOver ? 'white' : '#836eed',
                transition: 'color 0.25s ease',
              }}
            />
          )}
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 1,
            color: 'text.primary',
          }}
        >
          {isUploading
            ? 'Uploading...'
            : isDragOver
              ? 'Drop your files here'
              : 'Drop any PDF here'}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            maxWidth: 380,
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          {isUploading
            ? 'Hang tight, your documents are being processed.'
            : "Drag and drop your contracts, fee schedules, policies, or any clinic document. We\u2019ll make it make sense."}
        </Typography>

        {!isUploading && (
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={e => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
            sx={{
              borderColor: '#836eed',
              color: '#836eed',
              borderRadius: 2,
              px: 3,
              '&:hover': {
                borderColor: '#6246d8',
                bgcolor: 'rgba(130, 110, 230, 0.06)',
              },
            }}
          >
            Browse files
          </Button>
        )}
      </Box>
    </Box>
  );
}
